import React, { useEffect, useMemo, useState } from 'react';
import { FaSync, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { Dropdown } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const getAgeDays = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
};

const formatRelativeAge = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const matchesFreshnessFilter = (endpoint, freshnessFilter) => {
  if (freshnessFilter === 'all') return true;

  const ageDays = getAgeDays(endpoint.last_seen);
  if (freshnessFilter === 'unknown') return ageDays === null;
  if (ageDays === null) return false;
  if (freshnessFilter === 'healthy24h') return ageDays <= 1;
  if (freshnessFilter === 'stale7d') return ageDays > 1 && ageDays <= 7;
  if (freshnessFilter === 'stale30d') return ageDays > 7;
  return true;
};

const buildExecutiveSummary = ({ alerts, endpoints, selectedProviderName, topThreat }) => {
  const providerLabel = selectedProviderName || 'all providers';
  const criticalCount = alerts.filter((alert) => (alert.severity || '').toLowerCase() === 'critical').length;
  const unresolvedCount = alerts.filter((alert) => (alert.status || '').toLowerCase() !== 'resolved').length;
  const compromisedCount = endpoints.filter((endpoint) => endpoint.status === 'compromised').length;
  const threatLabel = topThreat || 'none';

  return `${providerLabel} currently has ${alerts.length} alerts, ${criticalCount} critical, ${unresolvedCount} unresolved, and ${compromisedCount} compromised endpoints. Dominant threat: ${threatLabel}.`;
};

const severityLevel = (value) => {
  const level = String(value || '').toLowerCase();
  if (level === 'critical') return 'critical';
  if (level === 'high') return 'high';
  if (level === 'medium') return 'medium';
  return 'low';
};

const statusLevel = (value) => {
  const level = String(value || '').toLowerCase();
  if (level === 'connected' || level === 'active' || level === 'resolved' || level === 'online') return 'good';
  if (level === 'blocked' || level === 'maintenance') return 'warn';
  if (level === 'failed' || level === 'inactive' || level === 'offline' || level === 'compromised') return 'bad';
  return 'neutral';
};

const riskLevel = (value) => {
  const score = Number(value) || 0;
  if (score >= 80) return 'bad';
  if (score >= 50) return 'warn';
  return 'good';
};

const toLabel = (value) =>
  String(value || 'unknown')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatTrendHour = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = String(date.getHours()).padStart(2, '0');
  return `${month}/${day} ${hour}:00`;
};

const extractActionLabel = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  if (typeof value === 'object') {
    const candidate = value.action || value.name || value.type || value.label || value.status;
    if (typeof candidate === 'string') {
      const normalized = candidate.trim();
      return normalized.length > 0 ? normalized : null;
    }
  }

  return null;
};

const hasDisplayValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const getTopEntries = (items, limit = 6) =>
  Array.from(items.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, value]) => ({ label, value }));

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedRecentAlertIds, setExpandedRecentAlertIds] = useState([]);
  const [expandedTopEndpointIds, setExpandedTopEndpointIds] = useState([]);
  const [expandedAlertIds, setExpandedAlertIds] = useState([]);
  const [expandedEndpointIds, setExpandedEndpointIds] = useState([]);

  const [selectedProviderId, setSelectedProviderId] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [osFilter, setOsFilter] = useState('all');
  const [threatTypeFilter, setThreatTypeFilter] = useState('all');
  const [freshnessFilter, setFreshnessFilter] = useState('all');

  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [dashboardStatus, setDashboardStatus] = useState({});
  const [dashboardTotalAlerts, setDashboardTotalAlerts] = useState(0);
  const [hierarchy, setHierarchy] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [responseTimeMs, setResponseTimeMs] = useState(0);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const startedAt = performance.now();
      const payload = await apiService.getEDRDashboard();
      setProviders(payload.providers || []);
      setConnections(payload.connections || []);
      setAlerts(payload.alerts || []);
      setEndpoints(payload.endpoints || []);
      setDashboardStatus(payload.status || {});
      setDashboardTotalAlerts(payload.total_alerts || 0);
      setHierarchy(payload.hierarchy || null);
      setLastSync(payload.last_sync || null);
      setResponseTimeMs(Math.round(performance.now() - startedAt));
      setError('');
    } catch (loadError) {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const selectedProvider = useMemo(() => {
    if (selectedProviderId === 'all') return null;
    return providers.find((provider) => provider.id === selectedProviderId) || null;
  }, [providers, selectedProviderId]);

  const scopedConnections = useMemo(() => {
    if (selectedProviderId === 'all') return connections;
    return connections.filter((item) => item.id === selectedProviderId);
  }, [connections, selectedProviderId]);

  const visibleAlerts = useMemo(() => {
    if (selectedProviderId === 'all') return alerts;
    return alerts.filter((alert) => alert.provider_id === selectedProviderId);
  }, [alerts, selectedProviderId]);

  const visibleEndpoints = useMemo(() => {
    if (selectedProviderId === 'all') return endpoints;
    return endpoints.filter((endpoint) => endpoint.provider_id === selectedProviderId);
  }, [endpoints, selectedProviderId]);

  const osOptions = useMemo(
    () => ['all', ...new Set(visibleEndpoints.map((endpoint) => endpoint.os).filter(Boolean))],
    [visibleEndpoints]
  );

  const threatTypeOptions = useMemo(
    () => ['all', ...new Set(visibleAlerts.map((alert) => alert.threat_type).filter(Boolean))],
    [visibleAlerts]
  );

  const filteredAlerts = useMemo(
    () =>
      visibleAlerts.filter((alert) => {
        const severityMatch = severityFilter === 'all' || (alert.severity || '').toLowerCase() === severityFilter;
        const statusMatch = statusFilter === 'all' || (alert.status || '').toLowerCase() === statusFilter;
        const threatMatch = threatTypeFilter === 'all' || alert.threat_type === threatTypeFilter;
        return severityMatch && statusMatch && threatMatch;
      }),
    [visibleAlerts, severityFilter, statusFilter, threatTypeFilter]
  );

  const filteredEndpoints = useMemo(
    () =>
      visibleEndpoints.filter((endpoint) => {
        const osMatch = osFilter === 'all' || endpoint.os === osFilter;
        const freshnessMatch = matchesFreshnessFilter(endpoint, freshnessFilter);
        return osMatch && freshnessMatch;
      }),
    [visibleEndpoints, osFilter, freshnessFilter]
  );

  const topThreatEntry = useMemo(() => {
    const map = new Map();
    filteredAlerts.forEach((alert) => {
      const key = alert.threat_type || 'unknown';
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value }))[0] || null;
  }, [filteredAlerts]);

  const topUserEntry = useMemo(() => {
    const map = new Map();
    filteredAlerts.forEach((alert) => {
      const key = alert.user_name || 'Unknown user';
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value }))[0] || null;
  }, [filteredAlerts]);

  const severityBreakdown = useMemo(
    () => ({
      critical: filteredAlerts.filter((alert) => (alert.severity || '').toLowerCase() === 'critical').length,
      high: filteredAlerts.filter((alert) => (alert.severity || '').toLowerCase() === 'high').length,
      medium: filteredAlerts.filter((alert) => (alert.severity || '').toLowerCase() === 'medium').length,
      low: filteredAlerts.filter((alert) => (alert.severity || '').toLowerCase() === 'low').length,
    }),
    [filteredAlerts]
  );
  const severityRows = [
    { key: 'critical', label: 'Critical', value: severityBreakdown.critical },
    { key: 'high', label: 'High', value: severityBreakdown.high },
    { key: 'medium', label: 'Medium', value: severityBreakdown.medium },
    { key: 'low', label: 'Low', value: severityBreakdown.low },
  ];
  const severityMax = Math.max(...severityRows.map((item) => item.value), 1);

  const threatTypeBreakdown = useMemo(() => {
    const map = new Map();
    filteredAlerts.forEach((alert) => {
      const key = alert.threat_type || 'unknown';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return getTopEntries(map, 8);
  }, [filteredAlerts]);
  const threatTrendRows = useMemo(() => {
    const hourlyBuckets = new Map();
    filteredAlerts.forEach((alert) => {
      const timestamp = alert.timestamp ? new Date(alert.timestamp) : null;
      if (!timestamp || Number.isNaN(timestamp.getTime())) return;

      timestamp.setMinutes(0, 0, 0);
      const bucketKey = timestamp.getTime();
      const current = hourlyBuckets.get(bucketKey) || { total: 0, critical: 0, active: 0 };

      current.total += 1;
      if ((alert.severity || '').toLowerCase() === 'critical') current.critical += 1;
      if ((alert.status || '').toLowerCase() !== 'resolved') current.active += 1;

      hourlyBuckets.set(bucketKey, current);
    });

    return Array.from(hourlyBuckets.entries())
      .sort((a, b) => b[0] - a[0])
      .slice(0, 5)
      .map(([bucketKey, values]) => ({
        time: formatTrendHour(bucketKey),
        total: values.total,
        critical: values.critical,
        active: values.active,
      }));
  }, [filteredAlerts]);
  const mitreSummary = useMemo(() => {
    const tacticMap = new Map();
    const techniqueMap = new Map();
    let mappedAlerts = 0;

    filteredAlerts.forEach((alert) => {
      const tactics = Array.isArray(alert.mitre_tactics) ? alert.mitre_tactics.filter(Boolean) : [];
      const techniques = Array.isArray(alert.mitre_techniques) ? alert.mitre_techniques.filter(Boolean) : [];

      if (tactics.length > 0 || techniques.length > 0) {
        mappedAlerts += 1;
      }

      tactics.forEach((tactic) => {
        tacticMap.set(tactic, (tacticMap.get(tactic) || 0) + 1);
      });

      techniques.forEach((technique) => {
        techniqueMap.set(technique, (techniqueMap.get(technique) || 0) + 1);
      });
    });

    return {
      mappedAlerts,
      uniqueTactics: tacticMap.size,
      uniqueTechniques: techniqueMap.size,
      topTactics: getTopEntries(tacticMap, 3),
      topTechniques: getTopEntries(techniqueMap, 3),
    };
  }, [filteredAlerts]);

  const topUsers = useMemo(() => {
    const map = new Map();
    filteredAlerts.forEach((alert) => {
      const key = alert.user_name || 'Unknown user';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return getTopEntries(map, 8);
  }, [filteredAlerts]);
  const resolvedVsActive = useMemo(() => ({
    resolved: filteredAlerts.filter((alert) => (alert.status || '').toLowerCase() === 'resolved').length,
    active: filteredAlerts.filter((alert) => (alert.status || '').toLowerCase() !== 'resolved').length,
  }), [filteredAlerts]);
  const responseActionBreakdown = useMemo(() => {
    const map = new Map();

    filteredAlerts.forEach((alert) => {
      const alertActions = new Set();
      const collectActions = (input) => {
        if (Array.isArray(input)) {
          input.forEach(collectActions);
          return;
        }

        const label = extractActionLabel(input);
        if (label) {
          alertActions.add(label);
        }
      };

      collectActions(alert.response_actions);
      collectActions(alert.response_action);
      collectActions(alert.actions);
      collectActions(alert.action);
      collectActions(alert.metadata?.response_actions);
      collectActions(alert.metadata?.response_action);
      collectActions(alert.metadata?.actions);
      collectActions(alert.metadata?.action);

      // Fallback to recommendations when explicit response actions are absent.
      if (alertActions.size === 0) {
        collectActions(alert.recommendations);
      }

      alertActions.forEach((action) => {
        map.set(action, (map.get(action) || 0) + 1);
      });
    });

    return getTopEntries(map, 8);
  }, [filteredAlerts]);

  const unresolvedAlerts = useMemo(
    () =>
      filteredAlerts
        .filter((alert) => (alert.status || '').toLowerCase() !== 'resolved')
        .sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0)),
    [filteredAlerts]
  );

  const topRiskEndpoints = useMemo(
    () => [...filteredEndpoints].sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0)).slice(0, 6),
    [filteredEndpoints]
  );
  const osBreakdown = useMemo(() => {
    const map = new Map();
    filteredEndpoints.forEach((endpoint) => {
      const key = endpoint.os || 'unknown';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return getTopEntries(map, 8);
  }, [filteredEndpoints]);
  const agentVersionBreakdown = useMemo(() => {
    const map = new Map();
    filteredEndpoints.forEach((endpoint) => {
      const key = endpoint.agent_version || 'unknown';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return getTopEntries(map, 8);
  }, [filteredEndpoints]);

  const freshnessBuckets = useMemo(() => {
    const buckets = {
      healthy24h: 0,
      stale7d: 0,
      stale30d: 0,
      unknown: 0,
    };

    filteredEndpoints.forEach((endpoint) => {
      const ageDays = getAgeDays(endpoint.last_seen);
      if (ageDays === null) {
        buckets.unknown += 1;
      } else if (ageDays <= 1) {
        buckets.healthy24h += 1;
      } else if (ageDays <= 7) {
        buckets.stale7d += 1;
      } else {
        buckets.stale30d += 1;
      }
    });

    return buckets;
  }, [filteredEndpoints]);
  const endpointFreshness = useMemo(
    () => [
      { label: 'Seen in 24h', value: freshnessBuckets.healthy24h },
      { label: '1-7 days stale', value: freshnessBuckets.stale7d },
      { label: '7+ days stale', value: freshnessBuckets.stale30d },
      { label: 'Unknown', value: freshnessBuckets.unknown },
    ],
    [freshnessBuckets]
  );

  const connectedCount = scopedConnections.filter((item) => item.connected).length;
  const connectionUptime = scopedConnections.length > 0
    ? (connectedCount / scopedConnections.length) * 100
    : null;

  const criticalCount = filteredAlerts.filter((alert) => (alert.severity || '').toLowerCase() === 'critical').length;
  const unresolvedCount = unresolvedAlerts.length;
  const compromisedCount = filteredEndpoints.filter((endpoint) => endpoint.status === 'compromised').length;
  const onlineCount = filteredEndpoints.filter((endpoint) => endpoint.status === 'online').length;
  const offlineCount = filteredEndpoints.filter((endpoint) => endpoint.status === 'offline').length;

  const providerHealth = scopedConnections.length > 0 ? (connectedCount / scopedConnections.length) * 100 : 100;
  const alertPenalty = Math.min(55, criticalCount * 7 + unresolvedCount * 2);
  const endpointPenalty = filteredEndpoints.length > 0 ? (compromisedCount / filteredEndpoints.length) * 45 : 0;
  const systemHealth = Math.round(clamp(providerHealth - alertPenalty - endpointPenalty, 0, 100));

  const generatedAssets = filteredAlerts.length;
  const totalEndpoints = filteredEndpoints.length;

  const actionQueue = [
    { label: 'Critical unresolved alerts', value: criticalCount },
    { label: 'Compromised endpoints', value: compromisedCount },
    {
      label: 'Recently offline endpoints',
      value: filteredEndpoints.filter((endpoint) => endpoint.status === 'offline' && (getAgeDays(endpoint.last_seen) || 0) <= 7).length,
    },
    {
      label: 'Stale endpoints over 7d',
      value: filteredEndpoints.filter((endpoint) => {
        const ageDays = getAgeDays(endpoint.last_seen);
        return ageDays !== null && ageDays > 7;
      }).length,
    },
  ];

  const changeWatch = [
    {
      label: 'New alerts in 24h',
      value: filteredAlerts.filter((alert) => {
        const ageDays = getAgeDays(alert.timestamp);
        return ageDays !== null && ageDays <= 1;
      }).length,
    },
    {
      label: 'Critical alerts in 24h',
      value: filteredAlerts.filter((alert) => {
        const ageDays = getAgeDays(alert.timestamp);
        return ageDays !== null && ageDays <= 1 && (alert.severity || '').toLowerCase() === 'critical';
      }).length,
    },
    {
      label: 'Endpoints active in 24h',
      value: filteredEndpoints.filter((endpoint) => {
        const ageDays = getAgeDays(endpoint.last_seen);
        return ageDays !== null && ageDays <= 1;
      }).length,
    },
    {
      label: 'Offline changes in 7d',
      value: filteredEndpoints.filter((endpoint) => {
        const ageDays = getAgeDays(endpoint.last_seen);
        return endpoint.status === 'offline' && ageDays !== null && ageDays <= 7;
      }).length,
    },
  ];
  const actionQueueMax = Math.max(...actionQueue.map((item) => item.value), 1);
  const changeWatchMax = Math.max(...changeWatch.map((item) => item.value), 1);

  const hierarchyAccounts = hierarchy?.accounts || [];
  const hierarchySites = hierarchy?.sites || [];
  const hierarchyGroups = hierarchy?.groups || [];
  const organizationalHierarchy = useMemo(
    () => [
      { label: 'Accounts', value: hierarchyAccounts.length },
      { label: 'Sites', value: hierarchySites.length },
      { label: 'Groups', value: hierarchyGroups.length },
      { label: 'Endpoints', value: filteredEndpoints.length },
      { label: 'Alerts', value: filteredAlerts.length },
    ],
    [hierarchyAccounts.length, hierarchySites.length, hierarchyGroups.length, filteredEndpoints.length, filteredAlerts.length]
  );
  const staleEndpoints = useMemo(
    () =>
      filteredEndpoints
        .map((endpoint) => {
          const ageDays = getAgeDays(endpoint.last_seen);
          return {
            ...endpoint,
            ageDays,
          };
        })
        .filter((endpoint) => endpoint.ageDays !== null && endpoint.ageDays > 7)
        .sort((a, b) => (b.ageDays || 0) - (a.ageDays || 0))
        .slice(0, 8),
    [filteredEndpoints]
  );
  const accountRiskHeatmap = useMemo(() => {
    const map = new Map();
    hierarchyAccounts.forEach((account) => {
      map.set(account.name || account.id, (account.risk_score || 0) + (account.alerts_count || 0));
    });
    return getTopEntries(map, 6);
  }, [hierarchyAccounts]);

  const siteRiskHeatmap = useMemo(() => {
    const map = new Map();
    hierarchySites.forEach((site) => {
      map.set(site.name || site.id, (site.risk_score || 0) + (site.alerts_count || 0));
    });
    return getTopEntries(map, 6);
  }, [hierarchySites]);

  const coverageGaps = useMemo(
    () => [
      { label: 'Compromised endpoints', value: filteredEndpoints.filter((endpoint) => endpoint.status === 'compromised').length },
      { label: 'Offline endpoints', value: filteredEndpoints.filter((endpoint) => endpoint.status === 'offline').length },
      { label: 'Unknown freshness', value: freshnessBuckets.unknown },
      { label: 'Accounts without alerts', value: hierarchyAccounts.filter((account) => (account.alerts_count || 0) === 0).length },
      { label: 'Sites without alerts', value: hierarchySites.filter((site) => (site.alerts_count || 0) === 0).length },
      { label: 'Groups without alerts', value: hierarchyGroups.filter((group) => (group.alerts_count || 0) === 0).length },
    ],
    [filteredEndpoints, freshnessBuckets.unknown, hierarchyAccounts, hierarchySites, hierarchyGroups]
  );
  const detailedAlerts = [...filteredAlerts]
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
    .slice(0, 20);
  const recentSecurityAlerts = detailedAlerts.slice(0, 3);
  const detailedEndpoints = [...filteredEndpoints]
    .sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0))
    .slice(0, 20);

  const executiveSummary = buildExecutiveSummary({
    alerts: filteredAlerts,
    endpoints: filteredEndpoints,
    selectedProviderName: selectedProvider?.name,
    topThreat: topThreatEntry?.label,
  });

  const toggleAlertExpand = (alertId) => {
    setExpandedAlertIds((prev) =>
      prev.includes(alertId) ? prev.filter((id) => id !== alertId) : [...prev, alertId]
    );
  };

  const toggleRecentAlertExpand = (alertId) => {
    setExpandedRecentAlertIds((prev) =>
      prev.includes(alertId) ? prev.filter((id) => id !== alertId) : [...prev, alertId]
    );
  };

  const toggleTopEndpointExpand = (endpointId) => {
    setExpandedTopEndpointIds((prev) =>
      prev.includes(endpointId) ? prev.filter((id) => id !== endpointId) : [...prev, endpointId]
    );
  };

  const toggleEndpointExpand = (endpointId) => {
    setExpandedEndpointIds((prev) =>
      prev.includes(endpointId) ? prev.filter((id) => id !== endpointId) : [...prev, endpointId]
    );
  };

  const toPrettyJson = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  };

  const renderSeverityBadge = (severity) => (
    <span className={`dash-ui-badge severity-${severityLevel(severity)}`}>{toLabel(severity)}</span>
  );

  const renderStatusBadge = (status) => (
    <span className={`dash-ui-badge status-${statusLevel(status)}`}>{toLabel(status)}</span>
  );

  const renderRiskBadge = (risk) => (
    <span className={`dash-ui-badge risk-${riskLevel(risk)}`}>{Number(risk) || 0}</span>
  );

  if (loading && providers.length === 0) {
    return (
      <div className="dash-ui-page">
        <LoadingSpinner message="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="dash-ui-page">
      <header className="dash-ui-appbar">
        <div className="dash-ui-appbar-title">
          <h2>Dashboard Overview</h2>
          <p>{executiveSummary}</p>
        </div>
        <div className="dash-ui-appbar-right">
          <div className="dash-ui-appbar-actions">
            <button type="button" aria-label="Notifications">
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <button type="button" aria-label="Help">
              <span className="material-symbols-outlined">help_outline</span>
            </button>
          </div>
          <div className="dash-ui-profile">
            <div>
              <p>{user?.username || 'Unknown User'}</p>
              <span>{user?.role || 'N/A'}</span>
            </div>
          </div>
        </div>
      </header>

      <section className="dash-ui-control-strip">
        <label className="dash-ui-switch">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(event) => setAutoRefresh(event.target.checked)}
          />
          <span>Auto-refresh every 30s</span>
        </label>
        <button type="button" className="dash-ui-refresh-btn" onClick={loadDashboardData} disabled={loading}>
          <FaSync />
          <span>{loading ? 'Refreshing...' : 'Refresh Now'}</span>
        </button>
        <button
          type="button"
          className={`dash-ui-outline-btn ${showAdvanced ? 'active' : ''}`}
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          {showAdvanced ? 'Hide Advanced Details' : 'Show Advanced Details'}
        </button>
      </section>

      {error && (
        <div className="dash-ui-error">
          <span className="material-symbols-outlined">warning</span>
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      <section className="dash-ui-filter-card">
        <div className="dash-ui-filter-grid">
          <label>
            <span>Provider</span>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
              >
                <span className="text-truncate">
                  {selectedProviderId === 'all' ? 'All Providers' : selectedProvider?.name || 'Select Provider'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                <button
                  type="button"
                  className={`report-dropdown-item ${selectedProviderId === 'all' ? 'selected' : ''}`}
                  onClick={() => setSelectedProviderId('all')}
                >
                  <span className="report-dropdown-icon">
                    {selectedProviderId === 'all' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>All Providers</span>
                </button>
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    className={`report-dropdown-item ${selectedProviderId === provider.id ? 'selected' : ''}`}
                    onClick={() => setSelectedProviderId(provider.id)}
                  >
                    <span className="report-dropdown-icon">
                      {selectedProviderId === provider.id ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                    </span>
                    <span>{provider.name} ({provider.provider})</span>
                  </button>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </label>
          <label>
            <span>Severity</span>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
              >
                <span className="text-truncate">
                  {severityFilter === 'all' ? 'All severities' : 
                   severityFilter === 'critical' ? 'Critical' :
                   severityFilter === 'high' ? 'High' :
                   severityFilter === 'medium' ? 'Medium' :
                   severityFilter === 'low' ? 'Low' : 'All severities'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                <button
                  type="button"
                  className={`report-dropdown-item ${severityFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => setSeverityFilter('all')}
                >
                  <span className="report-dropdown-icon">
                    {severityFilter === 'all' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>All severities</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${severityFilter === 'critical' ? 'selected' : ''}`}
                  onClick={() => setSeverityFilter('critical')}
                >
                  <span className="report-dropdown-icon">
                    {severityFilter === 'critical' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Critical</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${severityFilter === 'high' ? 'selected' : ''}`}
                  onClick={() => setSeverityFilter('high')}
                >
                  <span className="report-dropdown-icon">
                    {severityFilter === 'high' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>High</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${severityFilter === 'medium' ? 'selected' : ''}`}
                  onClick={() => setSeverityFilter('medium')}
                >
                  <span className="report-dropdown-icon">
                    {severityFilter === 'medium' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Medium</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${severityFilter === 'low' ? 'selected' : ''}`}
                  onClick={() => setSeverityFilter('low')}
                >
                  <span className="report-dropdown-icon">
                    {severityFilter === 'low' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Low</span>
                </button>
              </Dropdown.Menu>
            </Dropdown>
          </label>
          <label>
            <span>Status</span>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
              >
                <span className="text-truncate">
                  {statusFilter === 'all' ? 'All statuses' : 
                   statusFilter === 'active' ? 'Active' :
                   statusFilter === 'blocked' ? 'Blocked' :
                   statusFilter === 'resolved' ? 'Resolved' : 'All statuses'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                <button
                  type="button"
                  className={`report-dropdown-item ${statusFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => setStatusFilter('all')}
                >
                  <span className="report-dropdown-icon">
                    {statusFilter === 'all' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>All statuses</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${statusFilter === 'active' ? 'selected' : ''}`}
                  onClick={() => setStatusFilter('active')}
                >
                  <span className="report-dropdown-icon">
                    {statusFilter === 'active' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Active</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${statusFilter === 'blocked' ? 'selected' : ''}`}
                  onClick={() => setStatusFilter('blocked')}
                >
                  <span className="report-dropdown-icon">
                    {statusFilter === 'blocked' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Blocked</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${statusFilter === 'resolved' ? 'selected' : ''}`}
                  onClick={() => setStatusFilter('resolved')}
                >
                  <span className="report-dropdown-icon">
                    {statusFilter === 'resolved' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Resolved</span>
                </button>
              </Dropdown.Menu>
            </Dropdown>
          </label>
          <label>
            <span>OS</span>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
              >
                <span className="text-truncate">
                  {osFilter === 'all' ? 'All operating systems' : osFilter}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                {osOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`report-dropdown-item ${osFilter === option ? 'selected' : ''}`}
                    onClick={() => setOsFilter(option)}
                  >
                    <span className="report-dropdown-icon">
                      {osFilter === option ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                    </span>
                    <span>{option === 'all' ? 'All operating systems' : option}</span>
                  </button>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </label>
          <label>
            <span>Threat Type</span>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
              >
                <span className="text-truncate">
                  {threatTypeFilter === 'all' ? 'All threat types' : toLabel(threatTypeFilter)}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                {threatTypeOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`report-dropdown-item ${threatTypeFilter === option ? 'selected' : ''}`}
                    onClick={() => setThreatTypeFilter(option)}
                  >
                    <span className="report-dropdown-icon">
                      {threatTypeFilter === option ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                    </span>
                    <span>{option === 'all' ? 'All threat types' : toLabel(option)}</span>
                  </button>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </label>
          <label>
            <span>Freshness</span>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
              >
                <span className="text-truncate">
                  {freshnessFilter === 'all' ? 'All endpoints' : 
                   freshnessFilter === 'healthy24h' ? 'Seen in 24h' :
                   freshnessFilter === 'stale7d' ? '1-7 days stale' :
                   freshnessFilter === 'stale30d' ? '7+ days stale' :
                   freshnessFilter === 'unknown' ? 'Unknown' : 'All endpoints'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                <button
                  type="button"
                  className={`report-dropdown-item ${freshnessFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => setFreshnessFilter('all')}
                >
                  <span className="report-dropdown-icon">
                    {freshnessFilter === 'all' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>All endpoints</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${freshnessFilter === 'healthy24h' ? 'selected' : ''}`}
                  onClick={() => setFreshnessFilter('healthy24h')}
                >
                  <span className="report-dropdown-icon">
                    {freshnessFilter === 'healthy24h' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Seen in 24h</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${freshnessFilter === 'stale7d' ? 'selected' : ''}`}
                  onClick={() => setFreshnessFilter('stale7d')}
                >
                  <span className="report-dropdown-icon">
                    {freshnessFilter === 'stale7d' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>1-7 days stale</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${freshnessFilter === 'stale30d' ? 'selected' : ''}`}
                  onClick={() => setFreshnessFilter('stale30d')}
                >
                  <span className="report-dropdown-icon">
                    {freshnessFilter === 'stale30d' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>7+ days stale</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${freshnessFilter === 'unknown' ? 'selected' : ''}`}
                  onClick={() => setFreshnessFilter('unknown')}
                >
                  <span className="report-dropdown-icon">
                    {freshnessFilter === 'unknown' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Unknown</span>
                </button>
              </Dropdown.Menu>
            </Dropdown>
          </label>
        </div>
      </section>

      <section className="dash-ui-legend">
        <div>
          <span className="dash-ui-legend-label">Severity:</span>
          {renderSeverityBadge('critical')}
          {renderSeverityBadge('high')}
          {renderSeverityBadge('medium')}
          {renderSeverityBadge('low')}
        </div>
        <div>
          <span className="dash-ui-legend-label">Status:</span>
          {renderStatusBadge('connected')}
          {renderStatusBadge('active')}
          {renderStatusBadge('blocked')}
          {renderStatusBadge('failed')}
        </div>
        <div>
          <span className="dash-ui-legend-label">Risk:</span>
          <span className="dash-ui-badge risk-bad">80+</span>
          <span className="dash-ui-badge risk-warn">50-79</span>
          <span className="dash-ui-badge risk-good">0-49</span>
        </div>
      </section>

      <section className="dash-ui-hero-grid">
        <article className="dash-ui-metric-card">
          <p>Total Alerts</p>
          <h3>{generatedAssets.toLocaleString()}</h3>
          <div>
            <span className="material-symbols-outlined">warning</span>
            <span>{unresolvedCount} unresolved incidents</span>
          </div>
        </article>
        <article className="dash-ui-metric-card">
          <p>Total Endpoints</p>
          <h3>{totalEndpoints}</h3>
          <div>
            <span className="material-symbols-outlined">devices</span>
            <span>{onlineCount} online | {offlineCount} offline | {compromisedCount} compromised</span>
          </div>
        </article>
        <article className="dash-ui-metric-card dash-ui-metric-card-dark">
          <p>System Core Health</p>
          <h3>{systemHealth}%</h3>
          <div className="dash-ui-health-track">
            <div style={{ width: `${systemHealth}%` }} />
          </div>
        </article>
      </section>

      <section className="dash-ui-bento-grid">
        <article className="dash-ui-card dash-ui-chart-card">
          <div className="dash-ui-card-head">
            <div>
              <h4>Alert Severity Breakdown</h4>
              <p>Distribution of alerts by severity in current filter scope.</p>
            </div>
          </div>
          <div className="dash-ui-severity-chart">
            {severityRows.map((item) => {
              const height = item.value === 0 ? 8 : Math.max(16, Math.round((item.value / severityMax) * 100));
              return (
                <div key={item.key} className={`dash-ui-severity-chart-bar ${item.key}`}>
                  <div className="dash-ui-severity-value">{item.value}</div>
                  <div className="dash-ui-severity-column-wrap">
                    <div className="dash-ui-severity-column" style={{ height: `${height}%` }} />
                  </div>
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
          <p className="dash-ui-card-note">Total alerts in scope: {generatedAssets}</p>
        </article>

        <article className="dash-ui-card dash-ui-pipeline-card">
          <div className="dash-ui-card-head">
            <h4>Live Pipeline</h4>
            <span className="dash-ui-pulse" />
          </div>
          {unresolvedAlerts.length === 0 ? (
            <p className="dash-ui-empty">No unresolved alerts in this scope.</p>
          ) : (
            <div className="dash-ui-pipeline-list">
              {unresolvedAlerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="dash-ui-pipeline-item">
                  <div className={`dash-ui-severity-dot ${severityLevel(alert.severity)}`} />
                  <div>
                    <p>{toLabel(alert.threat_type || 'Unknown threat')}</p>
                    <span className="dash-ui-inline-meta">
                      {renderStatusBadge(alert.status)}
                      {renderRiskBadge(alert.risk_score)}
                      <em>{formatRelativeAge(alert.timestamp)}</em>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="dash-ui-insight-grid">
        <article className="dash-ui-card">
          <span className="material-symbols-outlined dash-ui-card-icon">psychology</span>
          <h5>Neural Optimization Insights</h5>
          <p>
            Health score is {systemHealth}%. Critical alerts: {criticalCount}. Online endpoints: {onlineCount} of {filteredEndpoints.length}.
          </p>
        </article>
        <article className="dash-ui-card">
          <span className="material-symbols-outlined dash-ui-card-icon">security</span>
          <h5>Endpoint Integrity Check</h5>
          <p>
            Connected providers: {connectedCount}/{scopedConnections.length}. Compromised endpoints: {compromisedCount}. Last sync: {lastSync ? new Date(lastSync).toLocaleString() : 'N/A'}.
          </p>
        </article>
        <article className="dash-ui-card dash-ui-card-visual">
          <div>
            <p>Architect Labs</p>
            <h5>Top Threat: {toLabel(topThreatEntry?.label || 'none')}</h5>
          </div>
          <div>
            <span>Top impacted user: {topUserEntry?.label || 'Unknown user'}</span>
          </div>
        </article>
      </section>

      <section className="dash-ui-bottom-grid">
        <article className="dash-ui-card">
          <h5>Action Queue</h5>
          <div className="dash-ui-line-bars dash-ui-line-bars-action">
            {actionQueue.map((item) => (
              <div key={item.label} className="dash-ui-line-row">
                <div className="dash-ui-line-head">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
                <div className="dash-ui-line-track">
                  <div
                    className="dash-ui-line-fill"
                    style={{ width: `${Math.round((item.value / actionQueueMax) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="dash-ui-card">
          <h5>Change Watch</h5>
          <div className="dash-ui-line-bars dash-ui-line-bars-change">
            {changeWatch.map((item) => (
              <div key={item.label} className="dash-ui-line-row">
                <div className="dash-ui-line-head">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
                <div className="dash-ui-line-track">
                  <div
                    className="dash-ui-line-fill"
                    style={{ width: `${Math.round((item.value / changeWatchMax) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="dash-ui-card">
          <h5>Threat Trends</h5>
          {threatTrendRows.length === 0 ? (
            <p className="dash-ui-empty">No trend data in current scope.</p>
          ) : (
            <div className="dash-ui-trend-table-wrap">
              <table className="dash-ui-trend-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Total</th>
                    <th>Critical</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {threatTrendRows.map((row) => (
                    <tr key={row.time}>
                      <td>{row.time}</td>
                      <td><span className="dash-ui-trend-pill total">{row.total}</span></td>
                      <td><span className="dash-ui-trend-pill critical">{row.critical}</span></td>
                      <td><span className="dash-ui-trend-pill active">{row.active}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </section>

      <section className="dash-ui-bottom-grid">
        <article className="dash-ui-card">
          <h5>Recent Security Alerts</h5>
          {recentSecurityAlerts.length === 0 ? (
            <p className="dash-ui-empty">No alerts available.</p>
          ) : (
            <div className="dash-ui-security-alert-list">
              {recentSecurityAlerts.map((alert, index) => {
                const alertKey = alert.id || `${alert.timestamp || 'ts'}-${alert.endpoint_name || 'endpoint'}-${index}`;
                const isExpanded = expandedRecentAlertIds.includes(alertKey);
                const providerLabel = alert.provider_name || alert.provider;
                const endpointName = alert.endpoint_name || alert.endpoint;
                const endpointOs = alert.endpoint_os || alert.os;
                const endpointIp = alert.endpoint_ip || alert.ip;
                const recommendations = Array.isArray(alert.recommendations)
                  ? alert.recommendations.filter((item) => hasDisplayValue(item))
                  : [];
                const mitreTactics = Array.isArray(alert.mitre_tactics)
                  ? alert.mitre_tactics.filter((item) => hasDisplayValue(item))
                  : [];
                const mitreTechniques = Array.isArray(alert.mitre_techniques)
                  ? alert.mitre_techniques.filter((item) => hasDisplayValue(item))
                  : [];
                const metadataEntries = alert.metadata && typeof alert.metadata === 'object' && !Array.isArray(alert.metadata)
                  ? Object.entries(alert.metadata).filter(([, value]) => hasDisplayValue(value)).slice(0, 14)
                  : [];

                return (
                  <article key={alertKey} className="dash-ui-security-alert">
                    <div className="dash-ui-security-alert-head">
                      <div className="dash-ui-security-badges">
                        {renderSeverityBadge(alert.severity)}
                        {hasDisplayValue(providerLabel) && (
                          <span className="dash-ui-security-chip dash-ui-security-chip-primary">{String(providerLabel).toUpperCase()}</span>
                        )}
                        {hasDisplayValue(alert.threat_type) && (
                          <span className="dash-ui-security-chip">{toLabel(alert.threat_type)}</span>
                        )}
                      </div>
                      <div className="dash-ui-security-actions">
                        {hasDisplayValue(alert.id) && (
                          <span className="dash-ui-security-id">ID: {alert.id}</span>
                        )}
                        <button
                          type="button"
                          className="dash-ui-table-toggle"
                          onClick={() => toggleRecentAlertExpand(alertKey)}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                      </div>
                    </div>

                    <div className="dash-ui-security-preview">
                      <p>{toLabel(alert.threat_type || 'Unknown threat')}</p>
                      <span>
                        {[endpointName, alert.user_name, hasDisplayValue(alert.timestamp) ? formatRelativeAge(alert.timestamp) : null]
                          .filter((item) => hasDisplayValue(item))
                          .join(' | ')}
                      </span>
                    </div>

                    {isExpanded && (
                      <>
                        <div className="dash-ui-security-grid">
                      <section className="dash-ui-security-section">
                        <h6>Alert Information</h6>
                        <div className="dash-ui-security-kv">
                          {hasDisplayValue(alert.id) && (
                            <div><span>Alert ID</span><strong>{alert.id}</strong></div>
                          )}
                          {hasDisplayValue(alert.timestamp) && (
                            <div><span>Timestamp</span><strong>{new Date(alert.timestamp).toLocaleString()}</strong></div>
                          )}
                          {hasDisplayValue(alert.status) && (
                            <div><span>Status</span><strong>{renderStatusBadge(alert.status)}</strong></div>
                          )}
                          {hasDisplayValue(alert.risk_score) && (
                            <div><span>Risk Score</span><strong>{`${Number(alert.risk_score) || 0}/100`}</strong></div>
                          )}
                          {hasDisplayValue(alert.confidence) && (
                            <div><span>Confidence</span><strong>{`${alert.confidence}%`}</strong></div>
                          )}
                        </div>
                      </section>

                      {(hasDisplayValue(endpointName)
                        || hasDisplayValue(endpointOs)
                        || hasDisplayValue(endpointIp)
                        || hasDisplayValue(alert.user_name)) && (
                        <section className="dash-ui-security-section">
                          <h6>Endpoint Information</h6>
                          <div className="dash-ui-security-kv">
                            {hasDisplayValue(endpointName) && (
                              <div><span>Endpoint</span><strong>{endpointName}</strong></div>
                            )}
                            {hasDisplayValue(endpointOs) && (
                              <div><span>Operating System</span><strong>{endpointOs}</strong></div>
                            )}
                            {hasDisplayValue(endpointIp) && (
                              <div><span>IP Address</span><strong>{endpointIp}</strong></div>
                            )}
                            {hasDisplayValue(alert.user_name) && (
                              <div><span>User</span><strong>{alert.user_name}</strong></div>
                            )}
                          </div>
                        </section>
                      )}

                      {(hasDisplayValue(alert.process_name) || hasDisplayValue(alert.process_path)) && (
                        <section className="dash-ui-security-section">
                          <h6>Process Information</h6>
                          <div className="dash-ui-security-kv">
                            {hasDisplayValue(alert.process_name) && (
                              <div><span>Process Name</span><strong>{alert.process_name}</strong></div>
                            )}
                            {hasDisplayValue(alert.process_path) && (
                              <div><span>Process Path</span><strong>{alert.process_path}</strong></div>
                            )}
                          </div>
                        </section>
                      )}

                      {(mitreTactics.length > 0 || mitreTechniques.length > 0) && (
                        <section className="dash-ui-security-section">
                          <h6>MITRE ATT&CK</h6>
                          <div className="dash-ui-security-kv">
                            {mitreTactics.length > 0 && (
                              <div><span>Tactics</span><strong>{mitreTactics.join(', ')}</strong></div>
                            )}
                            {mitreTechniques.length > 0 && (
                              <div><span>Techniques</span><strong>{mitreTechniques.join(', ')}</strong></div>
                            )}
                          </div>
                        </section>
                      )}
                    </div>

                    {(hasDisplayValue(alert.description) || recommendations.length > 0) && (
                      <section className="dash-ui-security-section dash-ui-security-section-full">
                        <h6>Additional Context</h6>
                        {hasDisplayValue(alert.description) && (
                          <p className="dash-ui-security-text">{alert.description}</p>
                        )}
                        {recommendations.length > 0 && (
                          <ul className="dash-ui-security-list">
                            {recommendations.map((recommendation, index) => (
                              <li key={`${alert.id || index}-${recommendation}`}>{recommendation}</li>
                            ))}
                          </ul>
                        )}
                      </section>
                    )}

                        {metadataEntries.length > 0 && (
                          <section className="dash-ui-security-section dash-ui-security-section-full">
                            <h6>Technical Details</h6>
                            <div className="dash-ui-security-meta">
                              {metadataEntries.map(([key, value]) => (
                                <span key={`${alertKey}-${key}`} className="dash-ui-security-meta-chip">
                                  {`${key}: ${typeof value === 'object' ? toPrettyJson(value) : String(value)}`}
                                </span>
                              ))}
                            </div>
                          </section>
                        )}
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </article>

        <article className="dash-ui-card">
          <h5>Top Risky Endpoints</h5>
          {topRiskEndpoints.length === 0 ? (
            <p className="dash-ui-empty">No endpoint risk data available.</p>
          ) : (
            <div className="dash-ui-endpoint-list">
              {topRiskEndpoints.map((endpoint, index) => {
                const endpointKey = endpoint.id || `${endpoint.name || 'endpoint'}-${index}`;
                const isExpanded = expandedTopEndpointIds.includes(endpointKey);
                const riskScore = Math.max(0, Math.min(100, Number(endpoint.risk_score) || 0));
                const providerLabel = endpoint.provider_name || endpoint.provider;

                return (
                  <article key={endpointKey} className="dash-ui-endpoint-card">
                    <div className="dash-ui-endpoint-head">
                      <div>
                        <p>{endpoint.name || endpoint.id || 'Unknown endpoint'}</p>
                        <span>
                          {[endpoint.os, endpoint.ip, hasDisplayValue(endpoint.last_seen) ? formatRelativeAge(endpoint.last_seen) : null]
                            .filter((item) => hasDisplayValue(item))
                            .join(' | ')}
                        </span>
                      </div>
                      <div className="dash-ui-security-actions">
                        {renderStatusBadge(endpoint.status || 'unknown')}
                        <button
                          type="button"
                          className="dash-ui-table-toggle"
                          onClick={() => toggleTopEndpointExpand(endpointKey)}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <>
                        <section className="dash-ui-security-section">
                          <h6>System Information</h6>
                          <div className="dash-ui-security-kv">
                            {hasDisplayValue(endpoint.id) && (
                              <div><span>Agent ID</span><strong>{endpoint.id}</strong></div>
                            )}
                            {hasDisplayValue(endpoint.os) && (
                              <div><span>Operating System</span><strong>{endpoint.os}</strong></div>
                            )}
                            {hasDisplayValue(endpoint.ip) && (
                              <div><span>IP Address</span><strong>{endpoint.ip}</strong></div>
                            )}
                            {hasDisplayValue(endpoint.mac) && (
                              <div><span>MAC Address</span><strong>{endpoint.mac}</strong></div>
                            )}
                            {hasDisplayValue(endpoint.agent_version) && (
                              <div><span>Agent Version</span><strong>{endpoint.agent_version}</strong></div>
                            )}
                            {hasDisplayValue(endpoint.last_seen) && (
                              <div><span>Last Seen</span><strong>{new Date(endpoint.last_seen).toLocaleString()}</strong></div>
                            )}
                          </div>
                        </section>

                        <section className="dash-ui-security-section dash-ui-security-section-full">
                          <h6>Risk Assessment</h6>
                          <div className="dash-ui-endpoint-risk-row">
                            <div className="dash-ui-endpoint-risk-track">
                              <div className="dash-ui-endpoint-risk-fill" style={{ width: `${riskScore}%` }} />
                            </div>
                            <strong>{`${riskScore}/100`}</strong>
                          </div>
                          {hasDisplayValue(providerLabel) && (
                            <span className="dash-ui-security-chip dash-ui-security-chip-primary">
                              {String(providerLabel).toUpperCase()}
                            </span>
                          )}
                        </section>
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </article>

        <article className="dash-ui-card dash-ui-mitre-card">
          <h5>MITRE Summary</h5>
          {filteredAlerts.length === 0 ? (
            <p className="dash-ui-empty">No alerts available for MITRE mapping.</p>
          ) : (
            <>
              <div className="dash-ui-stat-list dash-ui-mitre-summary">
                <div className="dash-ui-mitre-metric mapped"><span>Mapped alerts</span><strong>{mitreSummary.mappedAlerts}</strong></div>
                <div className="dash-ui-mitre-metric coverage">
                  <span>Coverage</span>
                  <strong>{`${Math.round((mitreSummary.mappedAlerts / filteredAlerts.length) * 100)}%`}</strong>
                </div>
                <div className="dash-ui-mitre-metric tactics"><span>Unique tactics</span><strong>{mitreSummary.uniqueTactics}</strong></div>
                <div className="dash-ui-mitre-metric techniques"><span>Unique techniques</span><strong>{mitreSummary.uniqueTechniques}</strong></div>
              </div>
              <h6 className="dash-ui-subtitle dash-ui-mitre-subtitle tactics">Top MITRE Tactics</h6>
              {mitreSummary.topTactics.length === 0 ? (
                <p className="dash-ui-empty">No MITRE tactics in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list dash-ui-mitre-list tactics">
                  {mitreSummary.topTactics.map((item) => (
                    <div key={`tactic-${item.label}`} className="dash-ui-mitre-item">
                      <span>{toLabel(item.label)}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
              <h6 className="dash-ui-subtitle dash-ui-mitre-subtitle techniques">Top MITRE Techniques</h6>
              {mitreSummary.topTechniques.length === 0 ? (
                <p className="dash-ui-empty">No MITRE techniques in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list dash-ui-mitre-list techniques">
                  {mitreSummary.topTechniques.map((item) => (
                    <div key={`technique-${item.label}`} className="dash-ui-mitre-item">
                      <span>{toLabel(item.label)}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </article>
      </section>

      {showAdvanced && (
        <>
          <section className="dash-ui-bottom-grid">
            <article className="dash-ui-card">
              <h5>Hierarchy Accounts</h5>
              {hierarchyAccounts.length === 0 ? (
                <p className="dash-ui-empty">No accounts in current hierarchy.</p>
              ) : (
                <div className="dash-ui-scroll-table">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Provider ID</th>
                        <th>Provider Name</th>
                        <th>Status</th>
                        <th>Endpoints</th>
                        <th>Alerts</th>
                        <th>Risk</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hierarchyAccounts.slice(0, 12).map((account) => (
                        <tr key={account.id}>
                          <td>{account.id}</td>
                          <td>{account.name || account.id}</td>
                          <td>{account.provider_id || 'N/A'}</td>
                          <td>{account.provider_name || 'N/A'}</td>
                          <td>{account.status || 'N/A'}</td>
                          <td>{account.endpoints_count || 0}</td>
                          <td>{account.alerts_count || 0}</td>
                          <td>{account.risk_score || 0}</td>
                          <td>{account.updated_at ? new Date(account.updated_at).toLocaleString() : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {hierarchyAccounts.slice(0, 4).map((account) => (
                <div key={`${account.id}-meta`} className="dash-ui-json-block">
                  <p>{account.name || account.id} metadata</p>
                  <pre>{toPrettyJson(account.metadata)}</pre>
                </div>
              ))}
            </article>

            <article className="dash-ui-card">
              <h5>Hierarchy Sites / Groups</h5>
              <div className="dash-ui-stat-list">
                <div><span>Sites in scope</span><strong>{hierarchySites.length}</strong></div>
                <div><span>Groups in scope</span><strong>{hierarchyGroups.length}</strong></div>
              </div>
              <div className="dash-ui-scroll-table">
                <table>
                  <thead>
                    <tr>
                      <th>Site / Group</th>
                      <th>Type</th>
                      <th>Provider ID</th>
                      <th>Account ID</th>
                      <th>Endpoints</th>
                      <th>Alerts</th>
                      <th>Risk</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hierarchySites.slice(0, 6).map((site) => (
                      <tr key={site.id}>
                        <td>{site.name || site.id}</td>
                        <td>SITE</td>
                        <td>{site.provider_id || 'N/A'}</td>
                        <td>{site.account_id || 'N/A'}</td>
                        <td>{site.endpoints_count || 0}</td>
                        <td>{site.alerts_count || 0}</td>
                        <td>{site.risk_score || 0}</td>
                        <td>{site.updated_at ? new Date(site.updated_at).toLocaleString() : 'N/A'}</td>
                      </tr>
                    ))}
                    {hierarchyGroups.slice(0, 6).map((group) => (
                      <tr key={group.id}>
                        <td>{group.name || group.id}</td>
                        <td>GROUP</td>
                        <td>{group.provider_id || 'N/A'}</td>
                        <td>{group.account_id || 'N/A'}</td>
                        <td>{group.endpoints_count || 0}</td>
                        <td>{group.alerts_count || 0}</td>
                        <td>{group.risk_score || 0}</td>
                        <td>{group.updated_at ? new Date(group.updated_at).toLocaleString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hierarchySites.slice(0, 3).map((site) => (
                <div key={`${site.id}-meta`} className="dash-ui-json-block">
                  <p>{site.name || site.id} metadata</p>
                  <pre>{toPrettyJson(site.metadata)}</pre>
                </div>
              ))}
              {hierarchyGroups.slice(0, 3).map((group) => (
                <div key={`${group.id}-meta`} className="dash-ui-json-block">
                  <p>{group.name || group.id} metadata</p>
                  <pre>{toPrettyJson(group.metadata)}</pre>
                </div>
              ))}
            </article>
          </section>

          <section className="dash-ui-bottom-grid">
            <article className="dash-ui-card">
              <h5>OS Breakdown</h5>
              {osBreakdown.length === 0 ? (
                <p className="dash-ui-empty">No endpoint OS data in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {osBreakdown.map((item) => (
                    <div key={item.label}>
                      <span>{toLabel(item.label)}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="dash-ui-card">
              <h5>Agent Version Breakdown</h5>
              {agentVersionBreakdown.length === 0 ? (
                <p className="dash-ui-empty">No endpoint agent version data in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {agentVersionBreakdown.map((item) => (
                    <div key={item.label}>
                      <span>{item.label || 'Unknown'}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
            <article className="dash-ui-card">
              <h5>Threat Type Breakdown</h5>
              {threatTypeBreakdown.length === 0 ? (
                <p className="dash-ui-empty">No threat data in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {threatTypeBreakdown.map((item) => (
                    <div key={item.label}>
                      <span>{toLabel(item.label)}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </section>

          <section className="dash-ui-bottom-grid">
            <article className="dash-ui-card">
              <h5>Top Users</h5>
              {topUsers.length === 0 ? (
                <p className="dash-ui-empty">No user attribution data in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {topUsers.map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="dash-ui-card">
              <h5>Resolved Vs Active</h5>
              <div className="dash-ui-stat-list">
                <div><span>{renderStatusBadge('resolved')}</span><strong>{resolvedVsActive.resolved}</strong></div>
                <div><span>{renderStatusBadge('active')}</span><strong>{resolvedVsActive.active}</strong></div>
              </div>
            </article>

            <article className="dash-ui-card">
              <h5>Endpoint Freshness</h5>
              <div className="dash-ui-stat-list">
                {endpointFreshness.map((item) => (
                  <div key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="dash-ui-bottom-grid">
            <article className="dash-ui-card">
              <h5>Account Risk Heatmap</h5>
              {accountRiskHeatmap.length === 0 ? (
                <p className="dash-ui-empty">No account hierarchy risk data.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {accountRiskHeatmap.map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="dash-ui-card">
              <h5>Site Risk Heatmap</h5>
              {siteRiskHeatmap.length === 0 ? (
                <p className="dash-ui-empty">No site hierarchy risk data.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {siteRiskHeatmap.map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="dash-ui-card">
              <h5>Coverage Gaps</h5>
              <div className="dash-ui-stat-list">
                {coverageGaps.map((item) => (
                  <div key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="dash-ui-bottom-grid">
            <article className="dash-ui-card">
              <h5>Stale Endpoints</h5>
              {staleEndpoints.length === 0 ? (
                <p className="dash-ui-empty">No stale endpoints (over 7 days) in current scope.</p>
              ) : (
                <div className="dash-ui-scroll-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Endpoint</th>
                        <th>Last Seen</th>
                        <th>Status</th>
                        <th>Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staleEndpoints.map((endpoint) => (
                        <tr key={endpoint.id}>
                          <td>{endpoint.name || endpoint.id}</td>
                          <td>{endpoint.ageDays === null ? 'Unknown' : `${Math.floor(endpoint.ageDays)}d ago`}</td>
                          <td>{renderStatusBadge(endpoint.status)}</td>
                          <td>{renderRiskBadge(endpoint.risk_score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </article>

            <article className="dash-ui-card">
              <h5>Organizational Hierarchy</h5>
              <div className="dash-ui-stat-list">
                {organizationalHierarchy.map((item) => (
                  <div key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="dash-ui-bottom-grid">
            <article className="dash-ui-card">
              <h5>Response Actions</h5>
              {responseActionBreakdown.length === 0 ? (
                <p className="dash-ui-empty">No response action data in current scope.</p>
              ) : (
                <div className="dash-ui-stat-list">
                  {responseActionBreakdown.map((item) => (
                    <div key={item.label}>
                      <span>{toLabel(item.label)}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </section>

          <section className="dash-ui-advanced-grid">
            <article className="dash-ui-card">
              <h5>Top-Level Dashboard Fields</h5>
              <div className="dash-ui-stat-list">
                <div><span>payload.total_alerts</span><strong>{dashboardTotalAlerts}</strong></div>
                <div><span>payload.status keys</span><strong>{Object.keys(dashboardStatus || {}).length}</strong></div>
                <div><span>payload.last_sync</span><strong>{lastSync ? new Date(lastSync).toLocaleString() : 'N/A'}</strong></div>
              </div>
              <div className="dash-ui-json-block">
                <p>payload.status</p>
                <pre>{toPrettyJson(dashboardStatus)}</pre>
              </div>
            </article>

            <article className="dash-ui-card">
              <h5>Detailed Alerts</h5>
              {detailedAlerts.length === 0 ? (
                <p className="dash-ui-empty">No alerts available for detailed view.</p>
              ) : (
                <div className="dash-ui-scroll-table">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Timestamp</th>
                        <th>Severity</th>
                        <th>Threat</th>
                        <th>Status</th>
                        <th>Endpoint</th>
                        <th>User</th>
                        <th>Risk</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailedAlerts.map((alert) => {
                        const isExpanded = expandedAlertIds.includes(alert.id);
                        return (
                          <React.Fragment key={alert.id}>
                            <tr>
                              <td>{alert.id}</td>
                              <td>{alert.timestamp ? new Date(alert.timestamp).toLocaleString() : 'N/A'}</td>
                              <td>{renderSeverityBadge(alert.severity)}</td>
                              <td>{toLabel(alert.threat_type || 'Unknown')}</td>
                              <td>{renderStatusBadge(alert.status)}</td>
                              <td>{alert.endpoint_name || 'N/A'}</td>
                              <td>{alert.user_name || 'N/A'}</td>
                              <td>{renderRiskBadge(alert.risk_score)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="dash-ui-table-toggle"
                                  onClick={() => toggleAlertExpand(alert.id)}
                                >
                                  {isExpanded ? 'Hide' : 'View'}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="dash-ui-expand-row">
                                <td colSpan={9}>
                                  <div className="dash-ui-expand-content">
                                    <div className="dash-ui-kv-grid">
                                      <div className="dash-ui-kv-item">
                                        <span>Provider</span>
                                        <strong>{alert.provider_name || alert.provider || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Provider ID</span>
                                        <strong>{alert.provider_id || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Endpoint OS</span>
                                        <strong>{alert.endpoint_os || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Endpoint IP</span>
                                        <strong>{alert.endpoint_ip || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Process</span>
                                        <strong>{alert.process_name || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Process Path</span>
                                        <strong>{alert.process_path || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Confidence</span>
                                        <strong>{alert.confidence ?? 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item dash-ui-kv-item-full">
                                        <span>Description</span>
                                        <strong>{alert.description || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item dash-ui-kv-item-full">
                                        <span>Recommendations</span>
                                        <strong>
                                          {Array.isArray(alert.recommendations) && alert.recommendations.length > 0
                                            ? alert.recommendations.join(' | ')
                                            : 'N/A'}
                                        </strong>
                                      </div>
                                      <div className="dash-ui-kv-item dash-ui-kv-item-full">
                                        <span>MITRE Tactics</span>
                                        <strong>
                                          {Array.isArray(alert.mitre_tactics) && alert.mitre_tactics.length > 0
                                            ? alert.mitre_tactics.join(', ')
                                            : 'N/A'}
                                        </strong>
                                      </div>
                                      <div className="dash-ui-kv-item dash-ui-kv-item-full">
                                        <span>MITRE Techniques</span>
                                        <strong>
                                          {Array.isArray(alert.mitre_techniques) && alert.mitre_techniques.length > 0
                                            ? alert.mitre_techniques.join(', ')
                                            : 'N/A'}
                                        </strong>
                                      </div>
                                      <div className="dash-ui-kv-item dash-ui-kv-item-full">
                                        <span>Metadata</span>
                                        <pre className="dash-ui-json-inline">{toPrettyJson(alert.metadata)}</pre>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </article>

            <article className="dash-ui-card">
              <h5>Detailed Endpoints</h5>
              {detailedEndpoints.length === 0 ? (
                <p className="dash-ui-empty">No endpoints available for detailed view.</p>
              ) : (
                <div className="dash-ui-scroll-table">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>OS</th>
                        <th>IP</th>
                        <th>Agent Version</th>
                        <th>Last Seen</th>
                        <th>Risk</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailedEndpoints.map((endpoint) => {
                        const isExpanded = expandedEndpointIds.includes(endpoint.id);
                        return (
                          <React.Fragment key={endpoint.id}>
                            <tr>
                              <td>{endpoint.id}</td>
                              <td>{endpoint.name || 'N/A'}</td>
                              <td>{renderStatusBadge(endpoint.status)}</td>
                              <td>{endpoint.os || 'N/A'}</td>
                              <td>{endpoint.ip || 'N/A'}</td>
                              <td>{endpoint.agent_version || 'N/A'}</td>
                              <td>{endpoint.last_seen ? new Date(endpoint.last_seen).toLocaleString() : 'N/A'}</td>
                              <td>{renderRiskBadge(endpoint.risk_score)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="dash-ui-table-toggle"
                                  onClick={() => toggleEndpointExpand(endpoint.id)}
                                >
                                  {isExpanded ? 'Hide' : 'View'}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="dash-ui-expand-row">
                                <td colSpan={9}>
                                  <div className="dash-ui-expand-content">
                                    <div className="dash-ui-kv-grid">
                                      <div className="dash-ui-kv-item">
                                        <span>Provider</span>
                                        <strong>{endpoint.provider_name || endpoint.provider || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Provider ID</span>
                                        <strong>{endpoint.provider_id || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>MAC Address</span>
                                        <strong>{endpoint.mac || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Account ID</span>
                                        <strong>{endpoint.account_id || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Site ID</span>
                                        <strong>{endpoint.site_id || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Group ID</span>
                                        <strong>{endpoint.group_id || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item">
                                        <span>Policy Mode</span>
                                        <strong>{endpoint.policy_mode || 'N/A'}</strong>
                                      </div>
                                      <div className="dash-ui-kv-item dash-ui-kv-item-full">
                                        <span>Raw Endpoint Record</span>
                                        <pre className="dash-ui-json-inline">{toPrettyJson(endpoint)}</pre>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
          </section>

        </>
      )}

      <footer className="dash-ui-footer">
        <div>
          <span>Providers connected: {connectedCount}/{scopedConnections.length}</span>
          <span>Alerts in scope: {filteredAlerts.length}</span>
          <span>Endpoints in scope: {filteredEndpoints.length}</span>
        </div>
        <div>
          <span>Connection uptime: {connectionUptime === null ? 'N/A' : `${connectionUptime.toFixed(2)}%`}</span>
          <span>Last sync: {lastSync ? new Date(lastSync).toLocaleString() : 'N/A'}</span>
          <span>Fetch latency: {responseTimeMs}ms</span>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
